import { useMemo } from "react";
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import type { RunnerOSBreakdown } from "@/lib/analytics/types";

interface RunnerOSBreakdownProps {
  breakdown: RunnerOSBreakdown;
  loading?: boolean;
}

const OS_COLORS: Record<string, string> = {
  Ubuntu: "var(--chart-1)",
  Windows: "var(--chart-2)",
  macOS: "var(--chart-4)",
};

export function RunnerOSBreakdownChart({ breakdown, loading }: RunnerOSBreakdownProps) {
  const data = useMemo(() => [
    { name: "Ubuntu", value: breakdown.ubuntu },
    { name: "Windows", value: breakdown.windows },
    { name: "macOS", value: breakdown.macos },
  ].filter((d) => d.value > 0), [breakdown]);

  const empty = data.length === 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Runner OS minutes</CardTitle>
        <CardDescription>
          Weighted billable minutes: <span className="font-mono">{breakdown.billableWeightedMinutes.toLocaleString()}</span>
          {" "}(ubuntu×1, windows×2, macos×10)
        </CardDescription>
      </CardHeader>
      <CardContent className="h-64">
        {loading ? (
          <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
            Loading from top 20 runs&hellip;
          </div>
        ) : empty ? (
          <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
            No billable minutes recorded.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" outerRadius={90} label>
                {data.map((d) => (
                  <Cell key={d.name} fill={OS_COLORS[d.name] ?? "var(--chart-3)"} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  background: "var(--popover)",
                  border: "1px solid var(--border)",
                  borderRadius: "0.375rem",
                  fontSize: "0.75rem",
                }}
                formatter={(v) => [`${Number(v ?? 0).toLocaleString()} min`, ""]}
              />
              <Legend wrapperStyle={{ fontSize: "0.75rem" }} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
